import { eAlertType } from 'src/providers/ToastProvider/Toast.enum';
import type {
  tThresholdRetryRejection,
  tThresholdTopUpState,
  tTopUpOutcome,
  tWalletChargeOutcome,
} from './Wallet.types';

/**
 * Qué arrastra cada desenlace del modal de recarga (RET-1928).
 *
 * POR QUÉ EXISTE. Todo flujo de la wallet que cambiaba algo terminaba en el
 * mismo `onClose()` que cerrar la ventana sin hacer nada, así que ninguna
 * pantalla podía confirmar lo ocurrido ni sabía qué volver a leer. RET-1870
 * abrió el primer canal —`onCharged`— pero atado a un solo caso; aquí vive la
 * tabla que lo vuelve una regla para todos.
 *
 * Sin React, sin timers, sin red: aquí están las DECISIONES. Los mecanismos ya
 * existen y no se tocan — la espera acotada en `Wallet.provider.tsx` y el
 * `ToastProvider` de la aplicación. Mismo reparto que `Wallet.settlement.ts`.
 *
 * La unión misma vive en `Wallet.types.ts`, con los demás tipos del slice.
 */

/**
 * Las claves del alta de recarga automática. La frase es la MISMA que el modal
 * acaba de pintar, con el mismo valor ya formateado, así que el aviso y el
 * resumen no pueden discrepar en una fecha que el cliente leyó segundos antes.
 */
export const WALLET_OUTCOME_NOTICE_KEY = 'topUp.start.explain';
/** La variante sin fecha, para cuando el back no la devolvió. */
export const WALLET_OUTCOME_NOTICE_NO_DATE_KEY = 'topUp.start.explainNoDate';
export const WALLET_RECURRING_NOTICE_TITLE_KEY =
  'autoTopUp.enabledNotice.title';

/** Las claves del aviso del cobro consumado (RET-1925). */
export const WALLET_CHARGED_NOTICE_TITLE_KEY = 'topUp.chargedNotice.title';
export const WALLET_CHARGED_NOTICE_KEY = 'topUp.chargedNotice.explain';

/** Las del 3DS pendiente, que afirma MENOS: el banco no ha contestado. */
export const WALLET_PENDING_NOTICE_TITLE_KEY = 'topUp.pendingNotice.title';
export const WALLET_PENDING_NOTICE_KEY = 'topUp.pendingNotice.explain';

/**
 * RET-2425 (R18): the threshold top-up's notices. `next` names the limit it
 * charges from; `charged` names the amount charged now, or the one already in
 * progress; `recurring` is the first charge of a recurring top-up that
 * replaced a threshold one.
 */
export const WALLET_THRESHOLD_NEXT_NOTICE_KEY =
  'autoTopUp.configuredNotice.next';
export const WALLET_THRESHOLD_CHARGED_NOTICE_KEY =
  'autoTopUp.configuredNotice.charged';
export const WALLET_RECURRING_FIRST_CHARGE_NOTICE_KEY =
  'autoTopUp.configuredNotice.recurring';

/**
 * RET-2426 (U4): los avisos de «Reintentar el cobro» que no son un cobro. El
 * título es el de la fila del switch («Recarga automática»): nombra de qué se
 * habla sin afirmar un resultado. La frase es una por motivo
 * (`autoTopUp.retryNotice.<reason>` y `…unconfirmed.<pause>`).
 */
export const WALLET_RETRY_NOTICE_TITLE_KEY = 'autoTopUp.title';
export const WALLET_RETRY_NOTICE_PREFIX = 'autoTopUp.retryNotice';

/**
 * RET-2426 (U6, KTD9): los avisos de la recarga única que terminó la pausa. El
 * título es el de la fila del switch. `resumed` afirma solo lo que dijo la
 * respuesta del pago; `resumedCharged` afirma además un cobro de la automática
 * y por eso solo sale con la relectura que lo confirma.
 */
export const WALLET_RESUMED_NOTICE_TITLE_KEY = 'autoTopUp.title';
export const WALLET_RESUMED_NOTICE_KEY = 'autoTopUp.resumedNotice.resumed';
export const WALLET_RESUMED_CHARGED_NOTICE_KEY =
  'autoTopUp.resumedNotice.resumedCharged';

/**
 * El color de cada motivo. ERROR solo cuando el cobro falló (banco o 3DS);
 * `not_paused` es INFO porque la recarga ya está bien; el resto es WARNING:
 * no se cobró y hay algo que esperar o pedir a soporte.
 */
const RETRY_REJECTION_ALERT: Record<tThresholdRetryRejection, eAlertType> = {
  declined: eAlertType.ERROR,
  authentication_failed: eAlertType.ERROR,
  authentication_unavailable: eAlertType.ERROR,
  not_paused: eAlertType.INFO,
  daily_cap: eAlertType.WARNING,
  charge_in_progress: eAlertType.WARNING,
  not_retryable: eAlertType.WARNING,
  frozen: eAlertType.WARNING,
  no_wapi: eAlertType.WARNING,
  multiple_subscriptions: eAlertType.WARNING,
  rejected: eAlertType.WARNING,
};

/**
 * Los dos status que el contrato del front nombra, y que esta tabla necesita
 * para decidir qué se afirma.
 *
 * Vivían como constantes locales del provider. Se mudan aquí porque QUÉ
 * SIGNIFICA un status es una decisión, no un mecanismo, y porque tenerlas en dos
 * módulos era la segunda copia del mismo literal: el provider decide con ellas
 * si arranca la espera y esta tabla decide con ellas qué dice el aviso, y las
 * dos respuestas tienen que hablar del mismo `pending`.
 *
 * `charged` es el único que significa que el dinero se movió y que viene fila de
 * ledger. `pending` es un reto 3DS que el banco no ha contestado: significa
 * TODAVÍA NO, no nunca. Cualquier otro no se reconoce y el provider lo reporta.
 */
export const CHARGED_STATUS = 'charged';
export const PENDING_STATUS = 'pending';

/**
 * El cobro que hay que asentar, con el monto que se cobró.
 *
 * UN objeto y no dos campos del mismo nivel, a propósito: los dos tienen que
 * viajar juntos siempre —el monto sin cobro no significa nada y el cobro sin
 * monto deja al candado ciego— y dos campos que deben moverse a la par son dos
 * campos que un día se mueven por separado.
 */
export type tSettlementRequest = {
  charge: tWalletChargeOutcome;
  /** Micro-unidades exactas, como string. */
  amountMicros: string;
};

/**
 * Un aviso, completamente resuelto por la tabla.
 *
 * `type` importa tanto como la frase. `SUCCESS` es "esto ya está"; el cobro es
 * "esto va en camino", y pintar el mismo verde para los dos borraría justamente
 * la distinción que RET-1925 existe para construir.
 */
export type tOutcomeNotice = {
  type: eAlertType;
  titleKey: string;
  messageKey: string;
  vars: Record<string, string>;
};

/**
 * RET-2426 (U6): lo que hace falta para confirmar, con una relectura del GET
 * posterior al pago, que la recarga por saldo bajo cobró. UN objeto: el
 * workspace y el contador de antes tienen que viajar juntos.
 */
export type tResumptionCheck = {
  /**
   * El workspace donde se pagó. Null cuando el llamador no lo dijo: se usa el
   * de la pantalla al recibir el desenlace.
   */
  workspaceId: string | null;
  /** `today.count` antes de pagar, o null si no se tenía. */
  chargesBefore: number | null;
};

/**
 * ¿La relectura muestra un cobro NUEVO de la recarga automática? (KTD9)
 *
 * Una sola prueba, y no es la predicción del pie: un `today.count` mayor que el
 * de antes de pagar (el cobro ya se pagó). Sin contador de antes no se compara
 * un número contra nada. El contador es de cobros automáticos PAGADOS hoy (el
 * pago que se pidió no cuenta, contrato A.3), así que el pago de esta misma
 * recarga no lo sube.
 *
 * Un `chargeInProgress` NO cuenta (revisión de PR B): es un intento en vuelo
 * de cualquier origen, que todavía puede rechazarse y cuyo monto cae a cero si
 * no se conoce (contrato A.3). «Cobró» sobre él afirmaría lo que el aviso del
 * 424 (`threshold-charge-in-progress`) se niega a afirmar.
 */
export function automaticChargeConfirmed(options: {
  state: tThresholdTopUpState;
  chargesBefore: number | null;
}): boolean {
  const { state, chargesBefore } = options;

  return chargesBefore !== null && state.today.count > chargesBefore;
}

export type tOutcomeEffects = {
  /**
   * El cobro que hay que asentar, o null.
   *
   * El COBRO y no un booleano, y esa es la diferencia que importa: con un
   * `settlesBalance: boolean` el consumidor tenía que volver a preguntar
   * `kind === 'charged'` para llegar al dato, y eran dos condiciones decidiendo
   * lo mismo. Un kind nuevo con la bandera prendida habría pasado el `never`
   * —la fila era obligatoria— y aun así se habría ignorado en silencio en el
   * provider. Entregando el dato ya extraído, la tabla es lo único que se lee.
   */
  settleCharge: tSettlementRequest | null;
  /** Invalida la configuración leída por `useAutoTopUp`. */
  invalidatesAutoTopUp: boolean;
  /**
   * Relee el saldo una vez (RET-2427, DS-16). Sólo para los desenlaces que
   * cambian SI algo va a rellenar el saldo, porque eso cambia el veredicto de
   * saldo bajo que viaja con él: pasar a la recarga frecuente lo rearma (F-9)
   * y programar la de saldo bajo lo apaga (RC-17). Un desenlace con
   * `settleCharge` ya relee por su espera, así que aquí va en false.
   */
  rereadsBalance: boolean;
  /**
   * RET-2426 (U6): el desenlace dice que la recarga por saldo bajo reanudó, y
   * lo que se anuncia depende de una relectura del GET que la tabla no puede
   * hacer (sin red). Quien pinta la hace y emite `threshold-resumed` con lo
   * que vio. Null en todos los demás.
   */
  confirmsResumption: tResumptionCheck | null;
  /**
   * El aviso ya resuelto —tipo, título, frase y variables—, o null cuando el
   * desenlace no se anuncia. Misma razón que arriba: quien pinta no vuelve a
   * discriminar.
   *
   * EL TIPO VIVE AQUÍ, y no en el hook que pinta (RET-1925). El hook asumía
   * `SUCCESS` para todo, que era correcto mientras el único desenlace anunciado
   * fuera un alta consumada. Con dos avisos de distinta certeza, dejar el tipo
   * en quien pinta habría metido un segundo `switch` sobre el kind fuera del
   * `never` — el mismo defecto que la tabla existe para cerrar. El título viaja
   * por la misma razón: era un literal en el hook, y con dos avisos ese literal
   * habría sido el título de los dos.
   */
  notice: tOutcomeNotice | null;
};

/**
 * La regla, en un solo lugar.
 *
 * Antes vivía repartida en lo que cada autor recordó: el cierre del modal
 * releía el SALDO en las dos pantallas que lo montaban, aunque agregar una
 * tarjeta no lo mueve y configurar una recarga automática tampoco. Se
 * refrescaba lo que no había cambiado y se dejaba viejo lo que sí.
 *
 * El `switch` es exhaustivo contra `never` a propósito: agregar un kind a la
 * unión sin darle fila aquí no compila. Es la única forma de que "cada
 * desenlace declara qué hay que releer" sea una garantía y no una costumbre.
 */
export function effectsFor(outcome: tTopUpOutcome): tOutcomeEffects {
  switch (outcome.kind) {
    case 'charged':
      // AVISA, y lo que afirma es el COBRO, no la acreditación (RET-1925, R1).
      //
      // CORRECCIÓN A LO QUE ESTA MISMA LÍNEA DECÍA. Aquí estaba escrito que un
      // cobro no debía avisar, porque anunciar "listo" mientras el ledger no
      // tiene la fila es la mitad del bug. Eso sigue siendo cierto y no es lo
      // que pasa aquí: el aviso dice que el cobro pasó y que el saldo viene en
      // camino. El comentario anterior le indicaba al siguiente que se callara,
      // y así se volvía la especificación equivocada de un ticket que pide
      // exactamente lo contrario.
      //
      // Y la otra mitad de la frase también era cierta: la forma correcta del
      // "hecho pero aún no visible" es un estado que persiste, no un toast que
      // se va a los 5 s. Por eso el aviso no viaja solo — arranca además la
      // espera, cuyo estado la tarjeta pinta mientras dura. El aviso explica el
      // instante; la tarjeta sostiene la ventana.
      //
      // INFO y no SUCCESS: falta la mitad de la operación.
      return {
        settleCharge: {
          charge: outcome.charge,
          amountMicros: outcome.amountMicros,
        },
        // RET-2426 (U6): un `thresholdTopUp` no nulo dice que había una pausa
        // y esta recarga la tocó, así que la tarjeta relee y la pausa
        // desaparece (o no, si el back dice que sigue). Con null, igual que
        // antes. `rereadsBalance` se queda en false: el `settleCharge` de
        // arriba ya relee el saldo con su espera, y esa lectura trae el
        // veredicto de saldo bajo con la pausa ya terminada.
        invalidatesAutoTopUp: outcome.thresholdTopUp != null,
        rereadsBalance: false,
        // Solo `resumed: true` pide la relectura que decide el aviso: con
        // `resumed: false` no hay nada que anunciar.
        confirmsResumption: outcome.thresholdTopUp?.resumed
          ? {
              workspaceId: outcome.resumption?.workspaceId ?? null,
              chargesBefore: outcome.resumption?.chargesBefore ?? null,
            }
          : null,
        notice: {
          type: eAlertType.INFO,
          // EL STATUS DECIDE QUÉ SE AFIRMA, y no mirarlo fue un defecto de la
          // primera versión (hallazgo #5 de la revisión). El kind `charged`
          // viaja para CUALQUIER POST exitoso, `pending` incluido — el modal
          // informa y no juzga, que es su contrato. Así que un reto 3DS que el
          // banco no ha contestado recibía el aviso "Cobro realizado · Tu cobro
          // se realizó", afirmando un cobro que todavía puede fallar.
          //
          // El aviso del pendiente afirma menos a propósito: que el banco está
          // confirmando, y que el saldo llega cuando confirme. Nunca que el
          // cobro se hizo.
          ...(outcome.charge.status === PENDING_STATUS
            ? {
                titleKey: WALLET_PENDING_NOTICE_TITLE_KEY,
                messageKey: WALLET_PENDING_NOTICE_KEY,
              }
            : {
                titleKey: WALLET_CHARGED_NOTICE_TITLE_KEY,
                messageKey: WALLET_CHARGED_NOTICE_KEY,
              }),
          // Sin variables: ninguna de las dos frases nombra el monto ni la
          // fecha. El monto que viaja en el desenlace es para el candado, no
          // para la copia — pintarlo exigiría la moneda, que este desenlace no
          // lleva, y formatearla aquí sería un segundo formateador de dinero.
          vars: {},
        },
      };

    case 'recurring-enabled':
      // No mueve el saldo, y por eso NO arranca la espera: el renglón se cobra
      // en la siguiente renovación, así que no viene acreditación alguna y una
      // espera de 15 s consumiría lecturas por algo que nunca llega.
      // Sin fecha se usa la variante que YA existe, no un guion ni un
      // "próximamente": `formatWalletDate` devuelve cadena vacía ante un valor
      // ausente o ilegible, y el trato de esta rebanada es esconder la fecha en
      // vez de prometer una que nadie puede sostener. La cadencia sí se sabe.
      return {
        settleCharge: null,
        invalidatesAutoTopUp: true,
        rereadsBalance: false,
        confirmsResumption: null,
        notice: {
          // SUCCESS, y la asimetría con el cobro es el punto: aquí la operación
          // terminó — el renglón quedó dado de alta y no falta nada por ocurrir.
          type: eAlertType.SUCCESS,
          titleKey: WALLET_RECURRING_NOTICE_TITLE_KEY,
          ...(outcome.startDateLabel
            ? {
                messageKey: WALLET_OUTCOME_NOTICE_KEY,
                vars: {
                  date: outcome.startDateLabel,
                  cadence: outcome.cadenceLabel,
                },
              }
            : {
                messageKey: WALLET_OUTCOME_NOTICE_NO_DATE_KEY,
                vars: { cadence: outcome.cadenceLabel },
              }),
        },
      };

    /*
     * RET-2425: the threshold top-up. Every row invalidates the automatic
     * top-up configuration: saving it can turn the recurring one off
     * (`replaceRecurring`), and the card reads both.
     */
    case 'threshold-configured':
      return {
        settleCharge: null,
        invalidatesAutoTopUp: true,
        rereadsBalance: true,
        confirmsResumption: null,
        notice: {
          type: eAlertType.SUCCESS,
          titleKey: WALLET_RECURRING_NOTICE_TITLE_KEY,
          messageKey: WALLET_THRESHOLD_NEXT_NOTICE_KEY,
          vars: { threshold: outcome.thresholdLabel },
        },
      };

    case 'threshold-charged':
      // The charge settles like a charged one-off: same wait, same rules
      // (`settleBalanceAfterCharge`). The notice is INFO, like the one-off:
      // charged is not yet credited.
      return {
        settleCharge: {
          charge: outcome.charge,
          amountMicros: outcome.amountMicros,
        },
        invalidatesAutoTopUp: true,
        rereadsBalance: false,
        confirmsResumption: null,
        notice: {
          type: eAlertType.INFO,
          titleKey: WALLET_RECURRING_NOTICE_TITLE_KEY,
          ...(outcome.charge.status === PENDING_STATUS
            ? { messageKey: WALLET_PENDING_NOTICE_KEY, vars: {} }
            : {
                messageKey: WALLET_THRESHOLD_CHARGED_NOTICE_KEY,
                vars: { amount: outcome.amountLabel },
              }),
        },
      };

    case 'threshold-charge-in-progress':
      // No charge to settle: this save did not charge, and the charge in
      // progress settles through its own webhook and push. The notice is the
      // pending one, not `configuredNotice.charged`: after a 424 the charge in
      // progress can still be declined, so «Cobramos» would claim too much.
      return {
        settleCharge: null,
        invalidatesAutoTopUp: true,
        rereadsBalance: true,
        confirmsResumption: null,
        notice: {
          type: eAlertType.INFO,
          titleKey: WALLET_PENDING_NOTICE_TITLE_KEY,
          messageKey: WALLET_PENDING_NOTICE_KEY,
          vars: {},
        },
      };

    case 'recurring-replaced-threshold':
      return {
        settleCharge: null,
        invalidatesAutoTopUp: true,
        rereadsBalance: true,
        confirmsResumption: null,
        notice: {
          type: eAlertType.SUCCESS,
          titleKey: WALLET_RECURRING_NOTICE_TITLE_KEY,
          ...(outcome.startDateLabel
            ? {
                messageKey: WALLET_RECURRING_FIRST_CHARGE_NOTICE_KEY,
                vars: { date: outcome.startDateLabel },
              }
            : {
                messageKey: WALLET_OUTCOME_NOTICE_NO_DATE_KEY,
                vars: { cadence: outcome.cadenceLabel },
              }),
        },
      };

    /*
     * RET-2426 (U4): las salidas de la pausa de saldo bajo. TODAS invalidan la
     * recarga automática, para que cualquier otra tarjeta montada relea la
     * pausa: un reintento, aunque falle, puede haberla cambiado (el 402 trae
     * un motivo nuevo, `not_paused` dice que alguien ya la resolvió).
     */
    case 'threshold-retry-charged':
      // Mismo aviso y misma espera que la recarga única: cobrado no es
      // acreditado, y `pending` es el banco que aún no contesta.
      return {
        settleCharge: {
          charge: outcome.charge,
          amountMicros: outcome.amountMicros,
        },
        invalidatesAutoTopUp: true,
        // `settleCharge` ya relee el saldo con su espera.
        rereadsBalance: false,
        confirmsResumption: null,
        notice: {
          type: eAlertType.INFO,
          ...(outcome.charge.status === PENDING_STATUS
            ? {
                titleKey: WALLET_PENDING_NOTICE_TITLE_KEY,
                messageKey: WALLET_PENDING_NOTICE_KEY,
              }
            : {
                titleKey: WALLET_CHARGED_NOTICE_TITLE_KEY,
                messageKey: WALLET_CHARGED_NOTICE_KEY,
              }),
          vars: {},
        },
      };

    case 'threshold-retry-rejected':
      // Relee el saldo: un 409 `not_paused` dice que la pausa ya terminó, y
      // eso cambia el veredicto de saldo bajo (DS-16).
      return {
        settleCharge: null,
        invalidatesAutoTopUp: true,
        rereadsBalance: true,
        confirmsResumption: null,
        notice: {
          type: RETRY_REJECTION_ALERT[outcome.reason],
          titleKey: WALLET_RETRY_NOTICE_TITLE_KEY,
          messageKey: `${WALLET_RETRY_NOTICE_PREFIX}.${outcome.reason}`,
          vars: {},
        },
      };

    case 'threshold-retry-unconfirmed':
      // Nunca afirma un cobro: la relectura solo dice si la pausa sigue. Relee
      // el saldo porque la pausa pudo terminar (DS-16).
      return {
        settleCharge: null,
        invalidatesAutoTopUp: true,
        rereadsBalance: true,
        confirmsResumption: null,
        notice: {
          type:
            outcome.pause === 'ended' ? eAlertType.INFO : eAlertType.WARNING,
          titleKey: WALLET_RETRY_NOTICE_TITLE_KEY,
          messageKey: `${WALLET_RETRY_NOTICE_PREFIX}.unconfirmed.${outcome.pause}`,
          vars: {},
        },
      };

    case 'threshold-disabled':
      // Sin aviso, igual que el apagado de la frecuente: el switch apagado es
      // la confirmación. Invalida, para que las otras tarjetas relean, y relee
      // el saldo: apagarla rearma el aviso de saldo bajo (RC-21, DS-16).
      return {
        settleCharge: null,
        invalidatesAutoTopUp: true,
        rereadsBalance: true,
        confirmsResumption: null,
        notice: null,
      };

    case 'threshold-resumed':
      // El aviso de la única que terminó la pausa (KTD9). `resumedCharged` solo
      // con la relectura que confirmó el cobro Y con su monto: sin cifra no se
      // puede escribir la frase, y cae a `resumed`, que no afirma de más. La
      // tarjeta ya releyó con el `charged` que lo precede; aquí relee otra vez
      // porque el cobro de la automática pudo salir después de esa lectura.
      // `rereadsBalance` en false por la misma razón que en `charged`: el saldo
      // de esa recarga ya lo asienta su espera.
      return {
        settleCharge: null,
        invalidatesAutoTopUp: true,
        rereadsBalance: false,
        confirmsResumption: null,
        notice: {
          type: eAlertType.SUCCESS,
          titleKey: WALLET_RESUMED_NOTICE_TITLE_KEY,
          ...(outcome.charged && outcome.amountLabel
            ? {
                messageKey: WALLET_RESUMED_CHARGED_NOTICE_KEY,
                vars: { amount: outcome.amountLabel },
              }
            : { messageKey: WALLET_RESUMED_NOTICE_KEY, vars: {} }),
        },
      };

    default: {
      const unhandled: never = outcome;

      return unhandled;
    }
  }
}
